import { Box, useColorMode } from '@chakra-ui/react'
import {
    Body,
    Button,
    ErrorMessage,
    FormProvider,
    Input,
    useYupForm,
    ValidatedInput,
    yup
} from '@missionlabs/react'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { AuthLayout } from 'shared/components/AuthLayout'
import { useResetPasswordMutation } from 'shared/store'

export interface ForgotPasswordFields {
    username: string
}

export const ForgotPassword: FC = () => {
    const { t } = useTranslation()
    const { colorMode } = useColorMode()

    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    const [resetPassword, { isSuccess }] = useResetPasswordMutation()

    const methods = useYupForm<ForgotPasswordFields>(
        yup.object({ username: yup.string().required() }),
        { defaultValues: { username: '' } }
    )
    const {
        formState: { isSubmitting },
        watch
    } = methods

    const onSubmit = async (data: ForgotPasswordFields) => {
        setErrorMessage(null)

        try {
            await resetPassword({ email: data.username }).unwrap()
        } catch (e) {
            console.error(e)
            setErrorMessage('data' in e ? e.data.message : e.message)
        }
    }

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
                <AuthLayout
                    label={
                        !isSuccess
                            ? t('auth.forgotPassword.title')
                            : t('auth.forgotPassword.successTitle')
                    }
                >
                    {!isSuccess && (
                        <>
                            {errorMessage && <ErrorMessage error={errorMessage} />}
                            <Body
                                size="sm"
                                alignSelf="start"
                                sx={{ color: `${colorMode}.tones.navy` }}
                            >
                                {t('auth.forgotPassword.subtitle')}
                            </Body>
                            <ValidatedInput name="username" label={t('Username/Email')}>
                                <Input
                                    autoComplete="username"
                                    placeholder={t('Enter username/email')}
                                />
                            </ValidatedInput>
                            <Box w="full" pt="8px">
                                <Button type="submit" size="lg" w="full" isLoading={isSubmitting}>
                                    {t('auth.forgotPassword.submitButton')}
                                </Button>
                            </Box>
                        </>
                    )}
                    {isSuccess && (
                        <>
                            <Body sx={{ color: `${colorMode}.coolGrey.80` }}>
                                {t('auth.forgotPassword.successMessage')}
                            </Body>
                            <Body variant="bold" sx={{ color: `${colorMode}.red.default` }}>
                                {watch('username')}
                            </Body>
                            <Body sx={{ color: `${colorMode}.coolGrey.80` }}>
                                {t('auth.forgotPassword.successInfo')}
                            </Body>
                        </>
                    )}
                    <Link to="/login">
                        <Body size="sm" variant="link-bold">
                            {t('auth.forgotPassword.rememberedLink')}
                        </Body>
                    </Link>
                </AuthLayout>
            </form>
        </FormProvider>
    )
}
